import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Wallet, BarChart3 } from 'lucide-react';
import { computeJobLaborCosts, formatUsd } from '@/lib/timeEntriesData';

export const JobLaborSummary = ({ entries = [] }) => {
  const { t } = useTranslation();
  const rows = computeJobLaborCosts(entries);
  const grandHours = rows.reduce((s, r) => s + r.hours, 0);
  const grandCost = rows.reduce((s, r) => s + r.cost, 0);
  const max = rows[0]?.cost || 0;

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('crewClock.labor_cost_per_job')}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {grandHours.toFixed(2)}h · {formatUsd(grandCost)}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="mt-4 text-sm text-muted-foreground py-6 text-center">
            {t('common.no_results')}
          </div>
        ) : (
          <ul className="mt-3 space-y-2.5" data-testid="job-labor-list">
            {rows.map((r) => {
              const pct = max > 0 ? Math.round((r.cost / max) * 100) : 0;
              return (
                <li
                  key={r.job}
                  className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3 py-2.5"
                  data-testid="job-labor-row"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {r.job}
                      </div>
                      <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        {r.entries} {t('common.items')} · {r.hours.toFixed(2)}h
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--success))]">
                        <Wallet size={12} />
                        {formatUsd(r.cost)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default JobLaborSummary;
